import React, { createContext, useContext, useState, useEffect } from 'react';
import { useShop } from './ShopContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const ThemeContext = createContext(null);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) throw new Error('useTheme must be used within ThemeProvider');
    return context;
};

function hexToHSL(hex) {
    let r = 0, g = 0, b = 0;
    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
        r = parseInt(hex.substring(1, 3), 16);
        g = parseInt(hex.substring(3, 5), 16);
        b = parseInt(hex.substring(5, 7), 16);
    }
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
            default: break;
        }
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function getContrastForeground(hex) {
    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '0 0% 10%' : '0 0% 98%';
}

function loadGoogleFont(fontName) {
    const id = `gfont-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@300;400;500;600;700&display=swap`;
    document.head.appendChild(link);
}

export const ThemeProvider = ({ children }) => {
    const { merchantId } = useShop();
    const [themeData, setThemeData] = useState(null);
    const [themeLoaded, setThemeLoaded] = useState(false);

    useEffect(() => {
        if (!merchantId) { setThemeLoaded(true); return; }
        const fetchTheme = async () => {
            try {
                const res = await axios.get(`${API_URL}/store/${merchantId}/theme`);
                setThemeData(res.data);
                applyTheme(res.data);
            } catch (err) {
                console.warn('[ThemeContext] Could not load theme, using defaults');
            } finally {
                setThemeLoaded(true);
            }
        };
        fetchTheme();
    }, [merchantId]);

    const applyTheme = (theme) => {
        if (!theme) return;
        const root = document.documentElement;
        const { colors, typography, style } = theme;

        if (colors) {
            root.style.setProperty('--primary', hexToHSL(colors.primary));
            root.style.setProperty('--primary-foreground', getContrastForeground(colors.primary));
            root.style.setProperty('--secondary', hexToHSL(colors.secondary));
            root.style.setProperty('--secondary-foreground', getContrastForeground(colors.secondary));
            root.style.setProperty('--accent', hexToHSL(colors.accent));
            root.style.setProperty('--accent-foreground', getContrastForeground(colors.accent));
            root.style.setProperty('--background', hexToHSL(colors.background));
            root.style.setProperty('--foreground', hexToHSL(colors.text));
            root.style.setProperty('--muted-foreground', hexToHSL(colors.muted));
            root.style.setProperty('--card', hexToHSL(colors.background));
            root.style.setProperty('--card-foreground', hexToHSL(colors.text));
            root.style.setProperty('--popover', hexToHSL(colors.background));
            root.style.setProperty('--popover-foreground', hexToHSL(colors.text));
        }

        if (typography) {
            loadGoogleFont(typography.heading_font);
            loadGoogleFont(typography.body_font);
            document.body.style.fontFamily = `'${typography.body_font}', sans-serif`;
            // Apply heading font via CSS class
            let styleEl = document.getElementById('theme-fonts');
            if (!styleEl) {
                styleEl = document.createElement('style');
                styleEl.id = 'theme-fonts';
                document.head.appendChild(styleEl);
            }
            styleEl.textContent = `.font-display { font-family: '${typography.heading_font}', serif !important; }`;
        }

        if (style) {
            root.style.setProperty('--radius', `${style.border_radius / 16}rem`);
        }

        const cust = theme.customization;
        if (cust?.favicon_url) {
            let link = document.querySelector('link[rel="icon"]');
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.head.appendChild(link);
            }
            link.href = cust.favicon_url.startsWith('http') ? cust.favicon_url : `${API_URL.replace('/api', '')}${cust.favicon_url}`;
        }
    };

    return (
        <ThemeContext.Provider value={{ themeData, themeLoaded }}>
            {children}
        </ThemeContext.Provider>
    );
};
